/**
 * Stock traffic light: a coloured dot followed by the band's title.
 *
 * The band itself is decided by the backend — the exact quantity is
 * commercially confidential and never reaches the browser — so this component
 * only maps a band onto its colour and its translated title. A null band means
 * the product shows no stock information at all.
 *
 * One component for every surface that shows stock (product page, quick order,
 * cart, cart drawer, both checkouts), so a band can never be coloured or titled
 * differently in two places by accident. The one deliberate difference is the
 * quick order's wording for the green band — see TITLES_BY_SURFACE.
 *
 * Information only: nothing here may gate adding to the cart or checking out.
 */

import React from "react";
import type { StockLevel } from "@/types/product";
import { useLocalizedTexts, t } from "@/utils/translations";
import styles from "./StockIndicator.module.css";

/**
 * Where the light is shown, which decides its wording and its styling:
 * - `"productPage"` — the product page's dark hero, directly under the price.
 * - `"quickOrder"` — a B2B quick order row (light surface).
 * - `"cart"` — a cart page row, the cart drawer or a checkout summary line
 *   (light surfaces).
 */
export type StockIndicatorSurface = "productPage" | "quickOrder" | "cart";

/** Dictionary key + English fallback for one band's title. */
interface StockTitle {
  key: string;
  fallback: string;
}

/** The product page's titles — the wording every other surface reuses. */
const PRODUCT_PAGE_TITLES: Record<StockLevel, StockTitle> = {
  outOfStock: {
    key: "Tracker.Products.Stock.OutOfStock",
    fallback: "Out of stock",
  },
  limitedQuantities: {
    key: "Tracker.Products.Stock.LimitedQuantities",
    fallback: "Limited quantities",
  },
  goodAvailability: {
    key: "Tracker.Products.Stock.GoodAvailability",
    fallback: "Good availability",
  },
};

/**
 * GROW-385 asked for the quick order's green band to read "In stock", while
 * its other bands — and every band on the cart and checkout — keep the product
 * page's labels. Only the title changes; the band, and so the colour, is the
 * same one the product page shows.
 */
const TITLES_BY_SURFACE: Record<
  StockIndicatorSurface,
  Record<StockLevel, StockTitle>
> = {
  productPage: PRODUCT_PAGE_TITLES,
  cart: PRODUCT_PAGE_TITLES,
  quickOrder: {
    ...PRODUCT_PAGE_TITLES,
    goodAvailability: {
      key: "Tracker.QuickOrder.InStock",
      fallback: "In stock",
    },
  },
};

interface StockIndicatorProps {
  /** The band to show, or null/undefined to show nothing. */
  level: StockLevel | null | undefined;
  surface: StockIndicatorSurface;
  /** Placement only (spacing within the host row); never colours. */
  className?: string;
}

export const StockIndicator: React.FC<StockIndicatorProps> = ({
  level,
  surface,
  className,
}) => {
  const localizedTexts = useLocalizedTexts();

  if (!level) return null;

  // A band the frontend does not know yet (added server-side ahead of a release)
  // must render nothing rather than an untitled dot.
  const title = TITLES_BY_SURFACE[surface][level];
  if (!title) return null;

  const rootClass = [
    styles.stock,
    surface === "productPage" ? styles.productPage : styles.compact,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // A span, not a paragraph: on the B2B checkout this sits inside a line's
  // name, which is itself inline content.
  return (
    <span
      className={rootClass}
      data-component="stock-indicator"
      data-stock-level={level}
      data-surface={surface}
    >
      {/* The colour repeats what the title already says, so it is decoration:
          nothing here is conveyed by colour alone. */}
      <span className={styles.dot} aria-hidden="true" />
      <span>{t(localizedTexts, title.key, title.fallback)}</span>
    </span>
  );
};

export default StockIndicator;
